import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Minus, Plus, Search } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Banner, Empty, Field, NativeSelect, Segmented, errMsg } from '@/components/hr/shared'
import { useCurrency } from '@/contexts/CurrencyContext'
import { todayISO } from '@/lib/hr'
import { defaultFare, parseMoney, parseQty, previewTotals } from '@/lib/trade'
import { toastHelpers } from '@/lib/toast-helpers'
import { cn } from '@/lib/utils'
import type { TradeAccount, TradeDelivery, TradeDeliveryInput, TradePriceRow } from '@/types/trade'
import { useTradeAccounts } from './TradePage'

// One delivery, entered the way the WhatsApp message reads: client and branch,
// date, morning or evening, then a quantity against each product, then the
// gadi fare. Prices come from the client's trade prices; the backend prices
// and totals the delivery on save.

type SlotChoice = 'morning' | 'evening' | 'other'

function slotChoice(slot: string): SlotChoice {
  return slot === 'morning' || slot === 'evening' ? slot : slot ? 'other' : 'morning'
}

export function DeliveryForm({ open, delivery, onClose, onSaved }: { open: boolean; delivery?: TradeDelivery; onClose: () => void; onSaved?: (d: TradeDelivery) => void }) {
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()
  const { accounts } = useTradeAccounts()
  const active = accounts.filter((a) => a.status === 'active' || a.id === delivery?.account_id)

  const [accountId, setAccountId] = useState('')
  const [branchId, setBranchId] = useState('')
  const [date, setDate] = useState(todayISO())
  const [slot, setSlot] = useState<SlotChoice>('morning')
  const [otherSlot, setOtherSlot] = useState('')
  const [fare, setFare] = useState('')
  const [fareTouched, setFareTouched] = useState(false)
  const [note, setNote] = useState('')
  const [qty, setQty] = useState<Record<string, string>>({})
  const [q, setQ] = useState('')
  const [showAll, setShowAll] = useState(false)

  // Reset whenever the sheet opens, from the delivery being edited if any.
  useEffect(() => {
    if (!open) return
    setAccountId(delivery?.account_id ?? (active.length === 1 ? active[0].id : ''))
    setBranchId(delivery?.branch_id ?? '')
    setDate(delivery?.delivery_date ?? todayISO())
    setSlot(slotChoice(delivery?.slot ?? ''))
    setOtherSlot(delivery && slotChoice(delivery.slot) === 'other' ? delivery.slot : '')
    setFare(delivery ? String(delivery.fare) : '')
    setFareTouched(!!delivery)
    setNote(delivery?.note ?? '')
    setQty(Object.fromEntries((delivery?.lines ?? []).map((l) => [l.product_id, String(l.qty)])))
    setQ('')
    setShowAll(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, delivery?.id])

  const acctQ = useQuery({
    queryKey: ['trade', 'account', accountId],
    queryFn: () => apiClient.tradeGetAccount(accountId),
    enabled: open && !!accountId,
  })
  const account = acctQ.data?.data as TradeAccount | undefined
  const branches = (account?.branches ?? []).filter((b) => b.is_active || b.id === delivery?.branch_id)

  // A client with one branch gets it picked for them.
  useEffect(() => {
    if (!account || account.id !== accountId) return
    if (branchId && !branches.some((b) => b.id === branchId)) setBranchId('')
    if (!branchId && branches.length === 1) setBranchId(branches[0].id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.id, branches.length])

  // The fare follows the branch, then the client, until someone types one.
  useEffect(() => {
    if (fareTouched || !account) return
    setFare(String(defaultFare(account, branches.find((b) => b.id === branchId))))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.id, branchId, fareTouched])

  const pricesQ = useQuery({
    queryKey: ['trade', 'prices', accountId],
    queryFn: () => apiClient.tradeListPrices(accountId),
    enabled: open && !!accountId,
  })
  const products = (pricesQ.data?.data as TradePriceRow[] | undefined) ?? []

  // A confirmed delivery keeps the price each line was confirmed at.
  const frozen = useMemo(() => {
    const m = new Map<string, number | null>()
    if (delivery && delivery.status !== 'draft') for (const l of delivery.lines ?? []) m.set(l.product_id, l.unit_price)
    return m
  }, [delivery])
  const priceOf = (p: TradePriceRow) => (frozen.has(p.product_id) ? frozen.get(p.product_id)! : p.effective_price)

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase()
    return products.filter(
      (p) =>
        (showAll || p.effective_price != null || parseQty(qty[p.product_id] ?? '') > 0) &&
        (!s || p.name.toLowerCase().includes(s) || (p.category ?? '').toLowerCase().includes(s)),
    )
  }, [products, q, showAll, qty])
  const unpricedCount = products.filter((p) => p.effective_price == null).length

  const chosen = products.filter((p) => parseQty(qty[p.product_id] ?? '') > 0)
  const fareNum = parseMoney(fare)
  const totals = previewTotals(
    chosen.map((p) => ({ qty: parseQty(qty[p.product_id] ?? ''), price: priceOf(p) })),
    fareNum ?? 0,
  )
  const slotValue = slot === 'other' ? otherSlot.trim() : slot

  const body = (confirm: boolean): TradeDeliveryInput => ({
    account_id: accountId,
    branch_id: branchId || null,
    delivery_date: date,
    slot: slotValue,
    fare: fareNum == null || Number.isNaN(fareNum) ? null : fareNum,
    note: note.trim(),
    lines: chosen.map((p) => ({ product_id: p.product_id, qty: parseQty(qty[p.product_id] ?? '') })),
    confirm,
  })

  const save = useMutation({
    mutationFn: (confirm: boolean) => (delivery ? apiClient.tradeUpdateDelivery(delivery.id, body(confirm)) : apiClient.tradeCreateDelivery(body(confirm))),
    onSuccess: (res) => {
      toastHelpers.success(res.message || 'Saved')
      qc.invalidateQueries({ queryKey: ['trade'] })
      if (res.data) onSaved?.(res.data)
      onClose()
    },
    onError: (err) => toastHelpers.error('Couldn’t save the delivery', errMsg(err)),
  })

  const bump = (id: string, by: number) => setQty((m) => ({ ...m, [id]: String(Math.max(0, parseQty(m[id] ?? '') + by) || '') }))
  const isDraft = !delivery || delivery.status === 'draft'
  const invalid = !accountId || !date || chosen.length === 0 || (fareNum != null && Number.isNaN(fareNum)) || (slot === 'other' && !slotValue)

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent size="lg">
        <SheetHeader>
          <SheetTitle>{delivery ? `Edit delivery ${delivery.delivery_no}` : 'New delivery'}</SheetTitle>
          <SheetDescription>
            {delivery && delivery.status !== 'draft'
              ? 'Changes are kept in the delivery’s history. Lines already confirmed keep their price.'
              : 'Enter it as it was agreed in the client’s WhatsApp group.'}
          </SheetDescription>
        </SheetHeader>
        <SheetBody className="space-y-5">
          {active.length === 0 ? (
            <Empty>No clients yet. Add one under Clients first.</Empty>
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Client">
                  <NativeSelect value={accountId} onChange={(e) => setAccountId(e.target.value)} disabled={!!delivery && delivery.status !== 'draft'}>
                    <option value="">Choose…</option>
                    {active.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </NativeSelect>
                </Field>
                <Field label="Branch">
                  <NativeSelect value={branchId} onChange={(e) => setBranchId(e.target.value)} disabled={!accountId}>
                    <option value="">{branches.length ? 'Choose…' : 'No branches'}</option>
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.label}
                      </option>
                    ))}
                  </NativeSelect>
                </Field>
                <Field label="Date">
                  <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                </Field>
                <Field label="When">
                  <div className="flex flex-wrap items-center gap-2">
                    <Segmented
                      value={slot}
                      onChange={setSlot}
                      options={[
                        { id: 'morning', label: 'Morning' },
                        { id: 'evening', label: 'Evening' },
                        { id: 'other', label: 'Other' },
                      ]}
                    />
                    {slot === 'other' && (
                      <Input value={otherSlot} onChange={(e) => setOtherSlot(e.target.value)} placeholder="e.g. After lunch" maxLength={40} className="h-9 w-40" />
                    )}
                  </div>
                </Field>
              </div>

              {accountId && (
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-medium">Products</span>
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products" className="h-9 w-52 pl-8" />
                    </div>
                  </div>
                  {pricesQ.isLoading ? (
                    <div className="text-sm text-muted-foreground">Loading…</div>
                  ) : shown.length === 0 ? (
                    <Empty>
                      {products.length && !showAll
                        ? 'No product has a trade price yet. Set them under Trade prices, or show every product.'
                        : 'No products match.'}
                    </Empty>
                  ) : (
                    <div className="divide-y rounded-lg border">
                      {shown.map((p) => {
                        const n = parseQty(qty[p.product_id] ?? '')
                        const price = priceOf(p)
                        return (
                          <div key={p.product_id} className={cn('flex items-center gap-3 px-3 py-2', n > 0 && 'bg-muted/40')}>
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-sm font-medium">{p.name}</div>
                              <div className="text-xs text-muted-foreground">
                                {p.category ?? 'No category'} ·{' '}
                                {price == null ? <span className="text-warning-fg">no trade price</span> : formatCurrency(price)}
                                {p.client_price != null && !frozen.has(p.product_id) && ' (this client’s price)'}
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => bump(p.product_id, -1)} disabled={n === 0} aria-label={`One fewer ${p.name}`}>
                                <Minus className="h-3.5 w-3.5" />
                              </Button>
                              <Input
                                inputMode="numeric"
                                value={qty[p.product_id] ?? ''}
                                onChange={(e) => setQty((m) => ({ ...m, [p.product_id]: e.target.value.replace(/[^0-9]/g, '') }))}
                                placeholder="0"
                                className="h-8 w-14 text-center tabular-nums"
                                aria-label={`Quantity of ${p.name}`}
                              />
                              <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => bump(p.product_id, 1)} aria-label={`One more ${p.name}`}>
                                <Plus className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                            <div className="w-24 text-right text-sm tabular-nums">{n > 0 && price != null ? formatCurrency(n * price) : ''}</div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                  {unpricedCount > 0 && (
                    <button type="button" className="text-xs text-muted-foreground underline-offset-2 hover:underline" onClick={() => setShowAll((v) => !v)}>
                      {showAll ? 'Hide products without a trade price' : `Show ${unpricedCount} product${unpricedCount === 1 ? '' : 's'} without a trade price`}
                    </button>
                  )}
                </div>
              )}

              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Delivery charge (gadi fare)" hint="0 for no fare">
                  <Input
                    inputMode="decimal"
                    value={fare}
                    onChange={(e) => {
                      setFare(e.target.value)
                      setFareTouched(true)
                    }}
                    placeholder="0"
                  />
                </Field>
                <Field label="Note" hint="Optional, for your team">
                  <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={1} />
                </Field>
              </div>

              {totals.missing > 0 && (
                <Banner tone="warn">
                  {totals.missing} product{totals.missing === 1 ? ' has' : 's have'} no trade price. You can save a draft, but it can’t be confirmed until{' '}
                  {totals.missing === 1 ? 'it is' : 'they are'} priced.
                </Banner>
              )}
            </>
          )}
        </SheetBody>
        <SheetFooter className="flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm">
            <span className="text-muted-foreground">
              {chosen.length} product{chosen.length === 1 ? '' : 's'} · {formatCurrency(totals.subtotal)} + {formatCurrency(totals.fare)} fare ={' '}
            </span>
            <span className="font-semibold tabular-nums">{formatCurrency(totals.total)}</span>
          </div>
          <div className="flex gap-2">
            {isDraft ? (
              <>
                <Button variant="outline" onClick={() => save.mutate(false)} disabled={invalid || save.isPending}>
                  Save draft
                </Button>
                <Button onClick={() => save.mutate(true)} disabled={invalid || totals.missing > 0 || save.isPending}>
                  Confirm delivery
                </Button>
              </>
            ) : (
              <Button onClick={() => save.mutate(false)} disabled={invalid || save.isPending}>
                Save changes
              </Button>
            )}
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
